import type { Messages } from "../fr";

export const leads: Messages["leads"] = {
  page: {
    title: "Leads — Hune",
    subject: "your leads",
    workspaceBanner: { before: "Pipeline of the client workspace ", after: " (read-only)." },
  },

  statuses: {
    new: "New",
    to_contact: "To contact",
    contacted: "Contacted",
    replied: "Replied",
    won: "Won",
    dismissed: "Dismissed",
  },

  dismissReasons: {
    off_target: { label: "Not my target", hint: "Sector or job outside the ICP" },
    wrong_size: { label: "Wrong size", hint: "Too small or too large for the offer" },
    competitor: { label: "Competitor", hint: "Does the same job as us" },
    already_customer: { label: "Already a customer", hint: "Already in our portfolio" },
    signal_weak: { label: "Weak signal", hint: "The trigger doesn’t hold up" },
    no_contact: { label: "No contact", hint: "No usable way to reach them" },
    bad_timing: { label: "Bad timing", hint: "Right target, not now" },
    other: { label: "Other reason", hint: "To be specified" },
  },

  assessmentAxes: {
    need_intensity: "Need intensity",
    solution_alignment: "Fit with the offer",
    decision_window: "Decision window",
    contact_path: "Contact path",
    source_confidence: "Source reliability",
  },

  readiness: {
    "Demande active": "Active demand",
    "Besoin explicite": "Explicit need",
    "Fenêtre favorable": "Favorable window",
  },

  runStatuses: {
    queued: "Queued",
    in_progress: "In progress",
    completed: "Completed",
    failed: "Failed",
    cancelled: "Cancelled",
  },

  table: {
    pipeline: "Pipeline",
    count: (n) => (n === 1 ? "1 lead" : `${n} leads`),
    unread: (n) => `${n} unread`,
    showAll: "Show all",
    showAllTitle: "Show all leads",
    unreadOnlyTitle: "Show only leads never opened",
    signalsThisWeek: (n) => (n === 1 ? "1 signal this week" : `${n} signals this week`),
    fedBy: "Fed by every pass of your monitoring",
    searchPlaceholder: "Organization, signal, category…",
    statusFilterLabel: "Filter by status",
    allStatuses: "All",
    layoutLabel: "Layout",
    cards: "Cards",
    table: "Table",
    favorites: "Favorites",
    archived: "Archived",
    empty: {
      before: "No leads yet. Your ",
      link: "monitoring",
      after: " will drop the organizations it detects here, on every pass.",
    },
    emptyArchived: "No archived leads.",
    emptyFiltered: "No leads match these filters.",
    updateFailed: "Couldn’t save the change.",
  },

  views: {
    addFavoriteNamed: (name) => `Add ${name} to favorites`,
    removeFavoriteNamed: (name) => `Remove ${name} from favorites`,
    archiveNamed: (name) => `Archive ${name}`,
    restoreNamed: (name) => `Restore ${name}`,
    scoreAria: (score) => `Score ${score} out of 100`,
    reactivated: "Reactivated",
    unread: "Unread",
    contactToFind: "To find",
    columns: {
      favorite: "Favorite",
      organization: "Organization",
      signal: "Signal",
      contact: "Contact",
      score: "Score",
      status: "Status",
      actions: "Actions",
    },
  },

  actions: {
    addFavorite: "Add to favorites",
    removeFavorite: "Remove from favorites",
    archive: "Archive",
    restore: "Restore",
  },

  sheet: {
    reactivated: "Reactivated",
    signalCount: (n) => (n === 1 ? "1 signal" : `${n} signals`),
    dismissed: "Dismissed",
    fixTargeting: "Fix targeting",
    tabsLabel: "Lead sections",
    tabs: { overview: "Overview", contacts: "Contacts", message: "Message", dossier: "Dossier" },
    noResearch: "No research has been run on this lead.",
    prepareApproach: "Prepare the approach",
  },

  actionMenu: {
    open: "Open actions",
    title: "Actions (⌘ K)",
    label: "Actions",
    messageCopied: "Message copied",
    copyMessage: "Copy message",
    refreshResearch: "Refresh the research",
    openSource: "Open the source",
    advance: "Move forward",
    dismiss: "Dismiss this lead…",
  },

  linearScoreAria: (label, value, maximum) => `${label}: ${value} out of ${maximum}`,

  contacts: {
    emptyTitle: "No decision cards available",
    emptyText: "Earlier contacts remain available below when a person was already linked to the lead.",
    pickerLabel: "Possible contacts",
    profiles: (n) => (n === 1 ? "1 profile" : `${n} profiles`),
    committee: "Buying committee",
    gaps: (n) => (n === 1 ? "1 item to confirm" : `${n} items to confirm`),
    fullDossier: "Full dossier",
    proofs: (n) => (n === 1 ? "1 proof point" : `${n} proof points`),
  },

  contactDetails: {
    summary: "Professional profile",
    responsibilities: "Documented responsibilities",
    background: "Relevant background",
    recentSignals: "Recent signals",
    source: "Source",
    personalization: "Personalization facts",
    publicPresence: "Public presence",
    otherIcebreakers: "Other icebreakers",
    sources: "Sources",
    caveats: "To confirm",
    searches: (n) => (n === 1 ? "1 web search run" : `${n} web searches run`),
    profileKinds: {
      linkedin_profile: "LinkedIn profile",
      linkedin_post: "LinkedIn post",
      interview: "Interview",
      article: "Article",
      podcast: "Podcast",
      conference: "Conference",
      social_post: "Post",
      other: "Public resource",
    },
  },

  message: {
    copied: "Copied",
    subject: "Subject",
    copySubject: "Copy subject",
    openInGmail: "Open in Gmail",
    otherMail: "Other email app",
    linkedinHandoff: (firstName) => `Copy and open ${firstName}’s profile`,
    linkedinCopied: "Message copied: now paste it into LinkedIn.",
    writing: "Writing…",
    rewrite: "Rewrite",
    writePerContact: "Write an email per contact",
    recipient: "Recipient",
    wordCount: (n) => `${n}-word email`,
    writeTo: (name) => `Write to ${name}`,
    approachMessage: "Outreach message",
    copyEmail: "Copy email",
    followUpHint: "Follow-up, three or four days later, in the same thread",
    copyFollowUp: "Copy follow-up",
    linkedinHint: "LinkedIn message, once the connection is accepted",
    copyLinkedin: "Copy LinkedIn message",
    whyThisEmail: "Why this email",
    copyMessage: "Copy message",
    legacyNote:
      "Shared message for the lead. An email written for each contact, from their profile, is shorter and more accurate.",
    noEmailYet: "No email has been written for this lead yet.",
    noContact: (organization) => `No contact identified at ${organization}: run the research from the Dossier tab.`,
  },

  osint: {
    starterEyebrow: "Sales research",
    starterTitle: "Find the signals and the right contacts",
    starterText:
      "Hune cross-checks the organization’s public results, hiring, projects, documents, public statements and vendor mentions, then prepares sourced questions and conversation angles.",
    running: "Research in progress…",
    start: "Run the research",
    title: "Prospect dossier",
    refreshing: "Refreshing…",
    refresh: "Refresh",
    summary: "One-minute read",
    takeaway: "Takeaway",
    noSignals: "No signal strong enough to qualify this organization.",
    questions: "Discovery questions",
    copied: "Copied",
    copy: "Copy",
    researchWork: "See the research work",
    gaps: "Points to check",
    queries: "Searches run",
    stages: {
      high_intent: "High intent",
      problem_aware: "Problem stated",
      trigger_present: "Trigger present",
      potential_fit: "Potential to confirm",
    },
    signalTypes: {
      vendor_echo: "Vendor mention",
      event: "Conference or trade show",
      hiring: "Hiring",
      transformation: "Transformation",
      public_document: "Public document",
      legal_event: "Legal event",
      procurement: "Public tender",
      advertising: "Campaign",
      technology: "Technology",
      public_voice: "Public statement",
      customer_pain: "Stated problem",
      other: "Other signal",
    },
    scoreAxes: {
      pain_strength: "Observed pain",
      product_fit: "Offer fit",
      timing: "Timing",
      public_reachability: "Approach route",
      evidence_quality: "Evidence quality",
    },
  },

  legacyContact: {
    fallbackName: "Contact",
    theContact: "the contact",
    quotedRole: (role) => `“${role}”`,
    searchOnLinkedin: (who) => `Search for ${who} on LinkedIn`,
    searchOnGoogle: "Search on Google",
  },

  dismiss: {
    title: (name) => `Dismiss ${name}`,
    description:
      "The reason isn’t a formality: it teaches the agent what to stop suggesting. Targeting reasons join your ICP exclusions.",
    noteLabel: "Details (optional)",
    notePlaceholder: "E.g. towns under 5,000 residents, no budget.",
    feedsIcp: "This reason will be added to the exclusions sent to the agent on the next pass.",
    cancel: "Cancel",
    confirm: "Dismiss",
  },

  overview: {
    scoreLabels: { priority: "Priority", promising: "Promising", toQualify: "To qualify" },
    previousLead: "Previous lead",
    previousTitle: "Previous (←)",
    nextLead: "Next lead",
    nextTitle: "Next (→)",
    progress: "Progress",
    noSignal: "No signal linked to this lead.",
    quoted: (text) => `“${text}”`,
    whyThem: "Why them",
    whyNow: "Why now",
    toVerify: "To verify",
    whoToContact: "Who to contact",
    roleToConfirm: "Role to confirm",
    publicResearch: "What public research reveals",
    scoreOutOf: "Score out of 100",
    status: "Status",
    approachRoute: "Approach route",
  },

  contact: {
    decisionRoles: {
      economic_buyer: "Budget holder",
      final_decision_maker: "Final decision-maker",
      business_owner: "Business owner",
      operational_owner: "Operational owner",
      influencer: "Influencer",
      executive_fallback: "Leadership",
    },
    confidence: { high: "Strong evidence", medium: "To confirm", low: "Weak evidence" },
    confidenceUnknown: "Confidence to assess",
    lanes: { decision: "Decision", business: "Business", operations: "Operations", influence: "Influence" },
    linkedinProfileOf: (name) => `${name}’s LinkedIn profile`,
    linkedinProfile: "LinkedIn profile",
    roleToConfirm: "Role to confirm",
    searchLinkedin: "Search on LinkedIn",
    searchGoogle: "Search on Google",
    firstToContact: "Contact first",
    priority: (rank) => `Priority ${rank}`,
    whyThisPerson: "Why this person",
    approachAngle: "Approach angle",
  },

  keyFacts: {
    title: "What matters for your offer",
    established: (count, total) => `${count} of ${total} ${total === 1 ? "criterion" : "criteria"} established`,
    decisive: "decisive",
    statuses: { confirmed: "Confirmed", inferred: "Inferred, to cross-check", unknown: "Not found" },
    notFound: "Not found",
    ask: "Ask: ",
  },

  source: {
    checkFailed: "Can’t check the page right now.",
    fallbackLabel: "Source",
    unreadableLink: (label) => `${label} — unreadable link`,
    loading: "Reading the page…",
    quoted: (text) => `“${text}”`,
    pageOk: "The page responds.",
    blocked: "This site blocks automated checks: open the page to view it.",
    pageDown: "This page no longer responds.",
    openOnQuote: "Open at the quoted passage",
    openSource: "Open the source",
  },

  tasks: {
    enrich: {
      done: "Research complete",
      failed: "The research didn’t finish",
      interrupted: "The connection dropped before the end: the research may have continued on the server.",
      retry: "Run the research again",
    },
    emails: {
      done: "Messages ready",
      failed: "The messages couldn’t be written",
      interrupted: "The connection dropped before the end: the writing may have continued on the server.",
      retry: "Write them again",
    },
    noReason: (status) => `The server gave no reason (error ${status}).`,
    reloadHint: (message) => `${message} Open the lead to see where it stands.`,
    open: "Open",
    retry: "Try again",
    reload: "Reload the lead",
  },

  orgLinks: { map: "Map" },

  offerLink: {
    unavailable: "This offer is no longer available: showing your current offer.",
  },

  export: {
    failed: "The export failed. Try again in a moment.",
    failedShort: "The export failed.",
    truncated: (limit) =>
      `The file stops at the ${limit} most recently seen leads: export one status at a time to get the rest.`,
    button: "Export as CSV",
    emptyPipeline: "No leads to export yet: your monitoring will add them here",
    emptyFiltered: "No leads to export with these filters",
    title: (n) => `Download ${n === 1 ? "1 lead" : `${n} leads`} as CSV (Excel, Google Sheets)`,
    headers: {
      organization: "Organization",
      website: "Website",
      status: "Status",
      score: "Score",
      latestSignal: "Latest signal",
      signalDate: "Signal date",
      signalSource: "Signal source",
      signalCount: "Signal count",
      firstSeen: "First seen",
      dismissReason: "Dismissal reason",
      contact: "Contact",
      role: "Role",
      email: "Email",
      linkedin: "LinkedIn",
      subject: "Message subject",
      message: "Message",
      followUp: "Follow-up",
      link: "Hune link",
    },
  },

  errors: {
    signIn: "Log in.",
    loadFailed: "Couldn’t load the leads. Check that the pipeline migrations have been applied.",
    exportSessionExpired: "Your session has expired: log in again to export your leads.",
    exportUnknownFilter: "Unknown export filter.",
    updateSignIn: "Log in to manage your leads.",
    invalidBody: "Invalid request body.",
    unknownStatus: "Unknown status.",
    notInTeam: "This person isn’t part of your team.",
    invalidFavorite: "Invalid favorite value.",
    invalidArchive: "Invalid archive value.",
    unknownDismissReason: "Unknown dismissal reason.",
    dismissReasonNeedsDismissed: "A dismissal reason only applies to a dismissed lead.",
    nothingToUpdate: "Nothing to update.",
    notFound: "Lead not found or access denied.",
    emailsSignIn: "Log in to write these emails.",
    writerNotConfigured: "The writing model isn’t configured on this server.",
    noContactToWrite: "No contact to write to: run the research on this lead first.",
    enrichSignIn: "Log in to research this lead.",
    searchNotConfigured: "Web search isn’t configured on this server.",
    researchFailed: "The searches couldn’t be completed. Try again in a moment.",
  },
};
